import React from 'react'
import { Link } from 'react-router-dom'
import PublicLayout from '../components/PublicLayout'
import Button from '../components/Button'
import SignupCTA from '../components/SignupCTA'
import { GRANTMAKERS, GRANTS_PAID_PROSE, fmt } from '../corpus'
import { RadarIcon, ShieldIcon, BoltIcon } from '../components/Icons'

const VALUES = [
  {
    icon: RadarIcon,
    title: 'Conservative matching',
    body: 'A junk recommendation is worse than a missed one. Praenum favors precision, so you can trust every grant in your digest.',
  },
  {
    // Was "No dashboards to babysit", which was the clearest statement anywhere
    // on the site of the misreading CLAUDE.md warns about: the no-login promise
    // is the floor, not a cap on what the product may become.
    icon: BoltIcon,
    title: 'The email is the floor',
    body: 'Once your profile is set, the value arrives in your inbox whether or not you ever log in. Everything the digest is built on is there when you want it, and ignorable when you do not.',
  },
  {
    icon: ShieldIcon,
    title: 'Built to last',
    body: 'Official data sources, reliable delivery, and transparent billing. Nonprofits deserve tools that just work.',
  },
]

export default function About() {
  return (
    <PublicLayout
      title="About"
      description={`Praenum monitors federal grant programs and the IRS filings of ${fmt(GRANTMAKERS)} grantmakers, then emails a ranked digest every Tuesday. Independently owned, self-hosted, no investors.`}
    >
      <section className="bg-gradient-to-b from-brand-100 to-white">
        <div className="max-w-3xl mx-auto px-4 pt-20 pb-12">
          <h1 className="text-4xl font-extrabold tracking-tight text-brand-900">
            Nonprofits shouldn't miss the grants they qualify for.
          </h1>
          <p className="mt-5 text-lg text-gray-600">
            Praenum exists for one reason: the organizations doing the most good with
            the least money are the ones with no time to monitor funding, so they miss it.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 py-12 prose-gray">
        <div className="space-y-5 text-gray-600 leading-relaxed">
          <h2 className="text-2xl font-bold text-brand-900">The problem</h2>
          <p>
            Grant research is nobody's full-time job. It is the fourth priority on a
            Tuesday, and it is the first thing to slip when the week gets away from you:
            as true at $6M as it is at $300K. Meanwhile the tools built to help cost
            hundreds of dollars a month and still expect someone to log in, run the
            searches, and track every deadline by hand.
          </p>

          <h2 className="text-2xl font-bold text-brand-900 pt-2">The approach</h2>
          <p>
            Praenum flips the model. Instead of another database you have to remember
            to check, it reads your mission and ranks the grantmakers most likely to fund
            work like yours, drawn from {fmt(GRANTMAKERS)} IRS filers and the{' '}
            {GRANTS_PAID_PROSE} grants they have actually paid, alongside the federal
            grants worth your time. Then it emails you a short digest every Tuesday. If
            one digest catches a single funder you would have missed, the product has paid
            for itself for years.
          </p>
          <p>
            It is deliberately <strong>not</strong> a grant-writing service. Praenum
            surfaces the opportunities; you and your team do the work you're great at.
          </p>

          <h2 className="text-2xl font-bold text-brand-900 pt-2">Independent by design</h2>
          <p>
            Praenum is independently owned and self-hosted in Massachusetts. It has taken
            no outside investment, which means no acquisition timeline and no growth
            target pushing the price up. That is the reason the pricing page looks the way it
            does, and the reason it can stay that way.
          </p>
          <p>
            The narrow scope is deliberate too. Monitoring done properly is worth more to
            a development office than a suite of features nobody has time to learn. It
            also means a support email reaches the people who build the product, rather
            than a ticket queue.
          </p>
        </div>
      </section>

      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <h2 className="text-2xl font-bold text-brand-900 text-center">What we believe</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {VALUES.map(v => (
              <div key={v.title} className="rounded-2xl bg-white border border-gray-200 p-7">
                <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-brand-100 text-brand-500 mb-4">
                  <v.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-gray-900">{v.title}</h3>
                <p className="mt-2 text-sm text-gray-600">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold text-brand-900">See it for your nonprofit</h2>
        <p className="mt-3 text-gray-600">
          Set up your profile in under five minutes and get your first digest.
        </p>
        <div className="mt-6 flex justify-center gap-4">
          <SignupCTA size="lg" />
          <Link to="/pricing">
            <Button variant="secondary" size="lg">See pricing</Button>
          </Link>
        </div>
      </section>
    </PublicLayout>
  )
}
